import {getTestProps} from '@benchling/js-test-utils';
import {Radio, Stack} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import SchemaCollaboratorsSection from 'collaborator-components/SchemaCollaboratorsSection';
import {
  ActionOrigin,
  COMMON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {EntitySummary} from 'constants/UserConstants';
import {
  DefaultSchemaCollaboratorsConfigurationSchemaType,
  ProcessClientOrigin,
  ProcessExecutionType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeFlowchartCreateEventMetadata,
  FLOWCHART_CONFIG_CREATE_ENDED_EVENT,
  FLOWCHART_CONFIG_CREATE_STARTED_EVENT,
} from 'performance/processProcessesEvents';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import * as ProcessTaskDefinitionDAO from 'processes/dao/ProcessTaskDefinitionDAO';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';
import CreateSchemaModal from 'schemas/CreateSchemaModal';
import NoOwnersSchemaMessage from 'schemas/NoOwnersSchemaMessage';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {Header3} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';
import {withLoadingScreen} from 'util/Promises';

import {ProcessStatusLifecyclesQuery} from './__generated/ProcessStatusLifecyclesQuery';
import {ProcessTaskSchemaEditorOrganizationsQuery} from './__generated/ProcessTaskSchemaEditorOrganizationsQuery';
import {isConfigurableTaskDefinition} from './graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {NameField, OwnerField, PrefixField} from './ProcessSchemaMetadataSectionRow';
import {
  PROCESS_TASK_SCHEMA_EDITOR_ORGANIZATIONS_QUERY,
  STATUS_LIFECYCLES_QUERY,
} from './ProcessTaskSchemaEditor';
import {DEFAULT_OUTPUT_SCHEMA_PREFIX, DEFAULT_TASK_SCHEMA_PREFIX} from './ProcessTaskSchemaEditorConstants';
import {cacheCreatedTaskDefinition, cacheUpdatedTaskSchema} from './ProcessTaskSchemaUtils';

enum FlowchartOutputsChoice {
  YES = 'outputs',
  NO = 'no-outputs',
}

export interface CreateFlowchartTaskSchemaModalProps {
  onRequestClose: () => void;
}

interface LoadedCreateFlowchartTaskSchemaModalProps extends CreateFlowchartTaskSchemaModalProps {
  organizationsForCollaborators: Array<OrganizationDefaultSchemaCollaboratorsConfiguration>;
  statusLifecyclesResult: Extract<
    ReturnType<typeof useBenchlingQuery<ProcessStatusLifecyclesQuery>>,
    {type: QueryResultType.SUCCESS}
  >;
}

function LoadedCreateFlowchartTaskSchemaModal({
  onRequestClose,
  organizationsForCollaborators,
  statusLifecyclesResult,
}: LoadedCreateFlowchartTaskSchemaModalProps): JSX.Element {
  const [name, setName] = useState<string | null>(null);
  const [taskGroupPrefix, setTaskGroupPrefix] = useState<string | null>(null);
  const [owner, setOwner] = useState<EntitySummary | null>(null);
  const [flowchartOutputsChoice, setFlowchartOutputsChoice] = useState<FlowchartOutputsChoice | undefined>(
    undefined
  );
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [errorBannerProps, setErrorBannerProps] = useState<InlineBannerProps | null>(null);
  const ownerChoices = getSchemaOwnerChoices();

  const isAccessPoliciesStepEnabled = BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI;

  const organizationForCollaborators =
    owner != null
      ? (organizationsForCollaborators.find((org) => org.id === owner.id) ?? organizationsForCollaborators[0])
      : organizationsForCollaborators[0];

  const areRequiredFieldsFilled = (): boolean =>
    name != null &&
    name !== '' &&
    taskGroupPrefix != null &&
    taskGroupPrefix !== '' &&
    owner != null &&
    flowchartOutputsChoice != null;

  function performValidation(): boolean {
    if (!areRequiredFieldsFilled()) {
      setErrorBannerProps({
        bannerType: InlineBannerType.ERROR,
        text: 'Please fill out all required fields before continuing.',
      });
      return false;
    }
    setErrorBannerProps(null);
    return true;
  }

  async function createFlowchart(collaborators: Array<SchemaCollaborator>): Promise<void> {
    assert(
      name != null && taskGroupPrefix != null && owner != null && flowchartOutputsChoice != null,
      'Expected all required fields to be filled'
    );
    const statusLifecycle = statusLifecyclesResult.data.processTaskStatusLifecycles.find(
      (lifecycle) => lifecycle.schemaExecutionType === ProcessExecutionType.FLOWCHART
    );
    assert(statusLifecycle != null, 'Expected status lifecycle to be found');
    setErrors({});
    logProfilerTimingEvent(FLOWCHART_CONFIG_CREATE_STARTED_EVENT);
    try {
      const taskDefinition = await withLoadingScreen(
        ProcessTaskDefinitionDAO.createProcessTaskDefinition({
          organizationId: owner.id,
          name,
          firstVersion: {
            taskSchemaSpec: {
              name,
              taskGroupPrefix,
              organizationId: owner.id,
              prefix: DEFAULT_TASK_SCHEMA_PREFIX,
              schemaFields: [],
              executionType: ProcessExecutionType.FLOWCHART,
              clientOrigin: ProcessClientOrigin.PROCESSES,
              canSetAssigneeOnTaskCreation: false,
              defaultResponsiblePartiesIds: [],
              statusLifecycleId: statusLifecycle.id,
              processOutputSchema:
                flowchartOutputsChoice === FlowchartOutputsChoice.YES
                  ? {
                      prefix: DEFAULT_OUTPUT_SCHEMA_PREFIX,
                      schemaFields: [],
                    }
                  : null,
            },
            applicationData: null,
          },
        }),
        'Creating flowchart...'
      );
      assert(isConfigurableTaskDefinition(taskDefinition), 'Expected task definition to be configurable');
      const newTaskSchema = taskDefinition.latestVersion.processTaskSchema;
      cacheCreatedTaskDefinition(taskDefinition);
      cacheUpdatedTaskSchema(newTaskSchema);
      // The create-task-definition API doesn't accept collaborators today, so apply the chosen
      // access policies via a follow-up update once the schema exists. If the update fails we
      // still want navigation to proceed (the schema itself was created successfully) — the user
      // can edit collaborators from the schema's settings page.
      if (isAccessPoliciesStepEnabled && collaborators.length > 0) {
        await ProcessSchemaDAO.updateCollaborators(newTaskSchema.id, {collaborators}).catch((e) => {
          notifications.notifyError(e);
        });
      }
      logProfilerTimingEvent(
        FLOWCHART_CONFIG_CREATE_ENDED_EVENT,
        composeFlowchartCreateEventMetadata({
          flowchartType: DefaultProcessesPlugin.FLOWCHART_TYPE_NAME,
          hasOutputs: flowchartOutputsChoice === FlowchartOutputsChoice.YES,
        })
      );
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_FLOWCHART, {
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        taskSchemaId: newTaskSchema.id,
        hasOutputs: flowchartOutputsChoice === FlowchartOutputsChoice.YES,
      });
      BenchlingUtil.navigate(null, processTaskSchemaURL(newTaskSchema.id));
      onRequestClose();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON) {
        setErrors(e.responseJSON.errors ?? {});
        notifications.error('The flowchart could not be created');
      } else {
        notifications.notifyError(e);
      }
    }
  }

  function renderMetadataSection(): JSX.Element {
    return (
      <>
        <div className={css(styles.fieldRowWrapper)}>
          <div className={css(styles.basicInfoField, styles.fieldWithMarginRight)}>
            <NameField
              label="Name"
              value={name ?? ''}
              isBeingCreated={true}
              onChange={(newName) => {
                setName(newName);
                setErrors({});
                setErrorBannerProps(null);
              }}
              error={errors.processTaskSchemaName}
              isRequired={true}
              dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'nameField'}}
            />
          </div>
          <div className={css(styles.basicInfoField)}>
            <PrefixField
              label="Task group prefix"
              value={taskGroupPrefix ?? ''}
              isBeingCreated={true}
              isRequired={true}
              onChange={(newPrefix) => {
                setTaskGroupPrefix(newPrefix);
                setErrors({});
                setErrorBannerProps(null);
              }}
              error={errors.taskGroupPrefix}
              dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'taskGroupPrefixField'}}
            />
          </div>
        </div>
        <div className={css(styles.ownerFieldWrapper)}>
          <OwnerField
            ownerChoices={ownerChoices}
            owner={owner}
            onOwnerChanged={(newOwner) => {
              setOwner(newOwner);
              setErrors({});
              setErrorBannerProps(null);
            }}
            isEditable={true}
            isRequired={true}
            className={css(styles.basicInfoField, styles.fieldWithMarginRight)}
            dataTestAttrs={{component: 'CreateFlowchartTaskSchemaModal', element: 'ownerField'}}
          />
        </div>
        <div>
          <Header3>
            Include outputs in the flowchart?
            <FieldRequiredIndicator />
          </Header3>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="You won't be able to change this after creating the flowchart."
          />
          <Radio.Group
            value={flowchartOutputsChoice}
            onChange={(value) => {
              setFlowchartOutputsChoice(value as FlowchartOutputsChoice);
              setErrors({});
              setErrorBannerProps(null);
            }}
            withAsterisk={true}
            style={{marginTop: '16px'}}
          >
            <Stack>
              <Radio
                value={FlowchartOutputsChoice.YES}
                label="Yes, I want to define the outputs this flowchart produces"
                {...getTestProps({
                  component: 'CreateFlowchartTaskSchemaModal',
                  element: 'flowchartOutputsChoice',
                  key: 'yesRadioButton',
                })}
                data-amp-safe={false}
              />
              <Radio
                value={FlowchartOutputsChoice.NO}
                label="No, I don't want to define the outputs this flowchart produces"
                {...getTestProps({
                  component: 'CreateFlowchartTaskSchemaModal',
                  element: 'flowchartOutputsChoice',
                  key: 'noRadioButton',
                })}
                data-amp-safe={false}
              />
            </Stack>
          </Radio.Group>
        </div>
      </>
    );
  }

  return (
    <CreateSchemaModal
      title="Create new flowchart"
      newFieldNames={[]}
      onRequestClose={onRequestClose}
      organization={organizationForCollaborators}
      createSchema={createFlowchart}
      isSchemaPermissionsEnabled={isAccessPoliciesStepEnabled}
      renderMetadataSection={renderMetadataSection}
      permissionsSettingsClass={SchemaCollaboratorsSection}
      bannerProps={errorBannerProps}
      performValidation={performValidation}
      defaultSchemaCollaboratorsSchemaType={DefaultSchemaCollaboratorsConfigurationSchemaType.PROCESS_TASK}
      // Match the original (pre-multi-step) modal width — the flowchart metadata only needs a
      // few fields, so the wider entity/result layout looks visually empty here.
      fullWidth={false}
    />
  );
}

export default function CreateFlowchartTaskSchemaModal({
  onRequestClose,
}: CreateFlowchartTaskSchemaModalProps): JSX.Element | null {
  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);
  const organizationsResult = useBenchlingQuery<ProcessTaskSchemaEditorOrganizationsQuery>(
    PROCESS_TASK_SCHEMA_EDITOR_ORGANIZATIONS_QUERY
  );

  useEffect(() => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.PROCEDURES, {
      modalType: 'create_flowchart_task_schema',
    });
  }, []);

  if (
    statusLifecyclesResult.type !== QueryResultType.SUCCESS ||
    organizationsResult.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const organizationsForCollaborators = organizationsResult.data.currentTenantOrganizations;

  if (organizationsForCollaborators.length === 0) {
    return <NoOwnersSchemaMessage pluralName="workflow task schemas" />;
  }

  return (
    <LoadedCreateFlowchartTaskSchemaModal
      onRequestClose={onRequestClose}
      organizationsForCollaborators={organizationsForCollaborators}
      statusLifecyclesResult={statusLifecyclesResult}
    />
  );
}

const styles = StyleSheet.create({
  ownerFieldWrapper: {
    width: '100%',
    marginBottom: '24px',
  },
  fieldRowWrapper: {
    width: '100%',
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  basicInfoField: {
    flex: 0.5,
  },
  fieldWithMarginRight: {
    marginRight: 16,
  },
});
